import _ from 'lodash';
import _isArray from 'lodash/isArray';
import _keys from 'lodash/keys';
import _pickBy from 'lodash/pickBy';
import { CategoryPotencyRanges, POTENCY_UNITS, POTENCY_UNITS_DISPLAY } from 'shared/constants';
import {
  formatWeightOptions,
  getFormattedPotency,
  getPotencyString,
  getPotencyValues,
  isPotencyProduct,
} from 'shared/helpers/products';
import { hasSaleSpecialForMenuType } from 'shared/helpers/specials';
import { isExcludedCannabinoid } from 'shared/constants/cannabinoids';
import { parseNoidAbbreviation } from 'shared/utils/misc-utils';

export { getFormattedPotency };

// Matches the canonical "Total Terpenes" compound name regardless of the whitespace/casing a given
// POS adapter's raw lab-result label uses. Confirmed via production data (ECOMM-13001 follow-up) that
// Armageddon's LeafLogix sync writes the per-option compound as the space-less "TotalTerpenes" (see
// TERPENE_NAMES in Armageddon/lib/menu_integration/adapters/leaflogix.rb), not "Total Terpenes" -- an
// exact-string match against the space-separated form therefore never matches any real product, and
// getOptionTotalTerpenes silently (and permanently) fell back to the stale parent-level aggregate this
// whole feature exists to avoid. Other POS adapters/future data may still use the spaced form (e.g. the
// canonical Postgres Terpene record's name is "Total Terpenes"), so this intentionally tolerates both
// rather than hardcoding either single literal.
const TOTAL_TERPENES_COMPOUND_NAME_PATTERN = /^total\s*terpenes$/i;

// Matches the aggregate THC/CBD compound entries, as distinct from individual cannabinoid
// compounds (THCA, CBDA, D9-THC, ...) that make up the breakdown list.
const THC_COMPOUND_NAME_PATTERN = /^thc$/i;
const CBD_COMPOUND_NAME_PATTERN = /^cbd$/i;

// Same whitespace/case mismatch as TOTAL_TERPENES_COMPOUND_NAME_PATTERN above, but for every
// individual compound name, not just the aggregate: Armageddon's raw per-option sync writes
// compact PascalCase ("BetaMyrcene", "BetaCaryophyllene" -- see TERPENE_NAMES in
// Armageddon/lib/menu_integration/adapters/leaflogix.rb), while the canonical library record
// used for description/aromas/effects lookups uses spaced names ("Beta Myrcene"). An exact-match
// lookup between the two would miss every multi-word compound and silently fall back to a blank
// info panel for all of them -- only single-word names like "Limonene" would ever match.
const normalizeCompoundName = (name) => (name ?? '').replace(/[\s-]+/g, '').toLowerCase();

// Library terpene names are often the bare compound ("Myrcene", "Pinene", "b-Pinene"), while POS
// sends the isomer ("BetaMyrcene", "AlphaPinene"); Armageddon bridges them with DB aliases the
// client can't see. Used only as a fallback after an exact normalized match fails.
const stripIsomerPrefix = (name) =>
  normalizeCompoundName((name ?? '').replace(/^\s*(alpha|beta|gamma|delta|[abgdαβγδ](?=[\s-]))[\s-]*/i, ''));

// Library cannabinoid names are "ABBR (Long name)", e.g. "THCA (Tetrahydrocannabinolic acid)" or
// "THC-D9 (...)", while POS sends the abbreviation ("THCA", "D9-THC"). Compare standardized
// abbreviations so the lookup works for real catalog data.
// Key on the leading token itself so abbreviations missing from CannabinoidAbbrev (e.g. "THCP")
// still match; parseNoidAbbreviation then standardizes the mapped ones (THC-D9 -> D9-THC).
const cannabinoidKey = (name) => {
  const token = /[\w-]+/.exec(name ?? '')?.[0] ?? '';
  return normalizeCompoundName(parseNoidAbbreviation(token));
};

function findOptionChild(product, selectedOptionValue) {
  if (!selectedOptionValue) {
    return null;
  }
  const children = product?.POSMetaData?.children;
  if (!_.isArray(children)) {
    return null;
  }
  return children.find((child) => child.option === selectedOptionValue) ?? null;
}

// The GraphQL type declares these as nullable lists ([Type], not [Type!]), so both a null list
// entry and a values array containing a null (e.g. an untested compound) are valid responses.
function maxNumeric(values) {
  const numbers = _.isArray(values) ? values.filter((v) => typeof v === 'number' && !Number.isNaN(v)) : [];
  return numbers.length > 0 ? _.max(numbers) : null;
}

function findCompoundValue(compounds, namePattern) {
  const compound = _.isArray(compounds) ? compounds.find((c) => c && namePattern.test(c?.name ?? '')) : null;
  const value = maxNumeric(compound?.values);
  if (value === null) {
    return null;
  }
  return { value, unit: compound.unit || POTENCY_UNITS.PERCENTAGE };
}

/**
 * Looks up the "Total Terpenes" compound recorded against the specific weight/package option the
 * shopper has selected (product.POSMetaData.children[]), rather than the product-wide aggregate
 * (product.totalTerpenes), which is merged across every weight option's batch at menu-sync time
 * and therefore can't reflect a per-weight lab result. See ECOMM-13001.
 *
 * Returns null when the option can't be matched or carries no per-option terpene data, so callers
 * can fall back to the product-level aggregate (e.g. for products/options predating this field).
 *
 * @param {Object} product
 * @param {string} [selectedOptionValue] - product.POSMetaData.children[].option, e.g. "3.5g"
 * @returns {{ value: number, unit: string } | null}
 */
export function getOptionTotalTerpenes(product, selectedOptionValue) {
  const matchingChild = findOptionChild(product, selectedOptionValue);
  return findCompoundValue(matchingChild?.terpenes, TOTAL_TERPENES_COMPOUND_NAME_PATTERN);
}

/**
 * Individual (non-aggregate) terpene compounds for the selected weight option, shaped to match
 * the Products_Terpene GraphQL type so the Terpenes breakdown accordion can vary by weight.
 * Falls back to product.terpenes when no per-option data exists. ECOMM-13001.
 */
export function getOptionTerpeneBreakdown(product, selectedOptionValue) {
  const matchingChild = findOptionChild(product, selectedOptionValue);
  const individualCompounds = matchingChild?.terpenes?.filter(
    (compound) => compound && !TOTAL_TERPENES_COMPOUND_NAME_PATTERN.test(compound?.name ?? '')
  );

  if (!_.isArray(individualCompounds) || individualCompounds.length === 0) {
    return product?.terpenes ?? [];
  }

  // Reuse the matching static library record (description/aromas/effects come from a separate
  // dataloader keyed by terpeneId, not present on the raw per-option compound) so clicking a
  // terpene still opens a populated info panel instead of a blank one. ECOMM-13001.
  const staticTerpenes = _.filter(product?.terpenes, Boolean);
  const terpeneName = (t) => t?.libraryTerpene?.name ?? t?.name;
  const staticByName = _.keyBy(staticTerpenes, (t) => normalizeCompoundName(terpeneName(t)));
  const staticByStripped = _.groupBy(staticTerpenes, (t) => stripIsomerPrefix(terpeneName(t)));

  return individualCompounds.map((compound) => {
    const unit = compound.unit || POTENCY_UNITS.PERCENTAGE;
    const exactMatch = staticByName[normalizeCompoundName(compound.name)];
    const strippedCandidates = staticByStripped[stripIsomerPrefix(compound.name)] ?? [];
    const staticMatch = exactMatch ?? (strippedCandidates.length === 1 ? strippedCandidates[0] : undefined);
    // On a fallback match keep the POS name, so e.g. AlphaPinene and BetaPinene don't both display
    // (and get keyed) as the library's "Pinene".
    const libraryTerpene = staticMatch?.libraryTerpene
      ? { ...staticMatch.libraryTerpene, ...(exactMatch ? {} : { name: compound.name }) }
      : { name: compound.name, aromas: [], description: null, effects: [] };
    return {
      ...staticMatch,
      // TerpeneList keys rows by id: only an exact match owns the library row's id. Fallback and
      // unmatched rows (e.g. AlphaPinene + BetaPinene both falling back to "Pinene") get a
      // per-compound id so keys stay unique; terpeneId still points at the shared library record.
      id: (exactMatch && staticMatch?.id) || `option-terpene-${compound.name}`,
      active: true,
      terpeneId: staticMatch?.terpeneId ?? null,
      name: compound.name,
      libraryTerpene,
      unit,
      unitSymbol: POTENCY_UNITS_DISPLAY[unit] || '%',
      value: maxNumeric(compound.values),
    };
  });
}

/**
 * Individual (non-THC/CBD) cannabinoid compounds for the selected weight option, shaped to match
 * the Products_CannabinoidV2 GraphQL type. Falls back to product.cannabinoidsV2/cannabinoids
 * when no per-option data exists. ECOMM-13001.
 */
export function getOptionCannabinoidBreakdown(product, selectedOptionValue) {
  const matchingChild = findOptionChild(product, selectedOptionValue);
  const individualCompounds = matchingChild?.cannabinoids?.filter(
    (compound) =>
      compound &&
      !THC_COMPOUND_NAME_PATTERN.test(compound?.name ?? '') &&
      !CBD_COMPOUND_NAME_PATTERN.test(compound?.name ?? '') &&
      // Also drop TAC (and anything else the grid excludes): LeafLogix writes it into every option,
      // and on its own it would block the static fallback while rendering nothing. Upper-case
      // first, since the CannabinoidAbbrev lookup behind isExcludedCannabinoid is case-sensitive.
      !isExcludedCannabinoid((compound?.name ?? '').toUpperCase())
  );

  if (!_.isArray(individualCompounds) || individualCompounds.length === 0) {
    return product?.cannabinoidsV2 ?? product?.cannabinoids ?? [];
  }

  // Reuse the matching static cannabinoid record's description for the same reason as terpenes
  // above -- otherwise opening THCA/CBGA/etc. shows an empty modal. ECOMM-13001.
  const staticList = product?.cannabinoidsV2 ?? product?.cannabinoids ?? [];
  const staticByName = _.keyBy(_.filter(staticList, Boolean), (c) => cannabinoidKey(c?.cannabinoid?.name));

  return individualCompounds.map((compound) => {
    const staticMatch = staticByName[cannabinoidKey(compound.name)];
    return {
      ...staticMatch,
      // Unique per compound name to avoid Apollo cache collisions on a shared falsy id.
      cannabinoidId: staticMatch?.cannabinoidId ?? `option-cannabinoid-${compound.name}`,
      active: true,
      unit: compound.unit || POTENCY_UNITS.PERCENTAGE,
      value: maxNumeric(compound.values),
      cannabinoid: staticMatch?.cannabinoid ?? { name: compound.name, description: null },
    };
  });
}

export function getPotencyStrings(product, selectedOptionValue) {
  const potencyValues = getPotencyValues(product);
  const potencyStrings = getFormattedPotency(product);
  const showTAC = isPotencyProduct(product) && parseFloat(potencyValues.TAC) > 0;

  const matchingChild = findOptionChild(product, selectedOptionValue);

  // Prefer the selected option's own batch value over the static cross-batch range, and gate
  // visibility on that resolved value (not just the static one) so a per-option-only value isn't
  // hidden behind an empty/zero static aggregate -- same pattern as TERPS below. ECOMM-13001.
  const optionTHC = findCompoundValue(matchingChild?.cannabinoids, THC_COMPOUND_NAME_PATTERN);
  const optionCBD = findCompoundValue(matchingChild?.cannabinoids, CBD_COMPOUND_NAME_PATTERN);
  const thcValue = optionTHC?.value ?? parseFloat(potencyValues.THC);
  const cbdValue = optionCBD?.value ?? parseFloat(potencyValues.CBD);
  const showTHC = isPotencyProduct(product) && thcValue > 0;
  const showCBD = isPotencyProduct(product) && cbdValue > 0;
  const thcString = optionTHC
    ? getPotencyString(parseFloat(optionTHC.value.toFixed(2)), optionTHC.unit)
    : potencyStrings.THC;
  const cbdString = optionCBD
    ? getPotencyString(parseFloat(optionCBD.value.toFixed(2)), optionCBD.unit)
    : potencyStrings.CBD;
  // optionTHC/optionCBD are never non-null with a non-numeric .value -- findCompoundValue (via
  // maxNumeric) already returns null in that case -- so the .toFixed(2) calls above are safe.

  const optionTerpenes = findCompoundValue(matchingChild?.terpenes, TOTAL_TERPENES_COMPOUND_NAME_PATTERN);
  const terpsRange = product?.totalTerpenes?.range;
  const rawTerpsValue =
    optionTerpenes?.value ?? (_.isArray(terpsRange) && terpsRange.length > 0 ? _.max(terpsRange) : 0);
  const terpsValue = parseFloat(parseFloat(rawTerpsValue).toFixed(2));
  const terpsUnit = optionTerpenes?.unit || product?.totalTerpenes?.unit || POTENCY_UNITS.PERCENTAGE;
  const showTerps = isPotencyProduct(product) && terpsValue > 0;

  return {
    THC: showTHC ? thcString : null,
    CBD: showCBD ? cbdString : null,
    TAC: showTAC ? potencyStrings.TAC : null,
    TERPS: showTerps ? getPotencyString(terpsValue, terpsUnit) : null,
  };
}

export function rangeIsValid(range, maxRange) {
  if (!range || _.some(range, (value) => !_.inRange(value, maxRange[0], maxRange[1] + 1))) {
    return false;
  }
  return true;
}

export function getPricesForMenuType({ product, menuType, returnSaleSpecialPrices = false }) {
  const { recPrices, medicalPrices, recSpecialPrices, medicalSpecialPrices } = product;
  const isMed = menuType === 'med';
  const prices = isMed ? medicalPrices : recPrices;
  const specialPrices = isMed ? medicalSpecialPrices : recSpecialPrices;

  return returnSaleSpecialPrices ? specialPrices : prices;
}

export function getOptionsQualifyingForOffer({ product, offerId }) {
  const matchingOffer = _.find(
    product.specialData?.bogoSpecials,
    ({ specialId, sourceId }) => specialId === offerId || sourceId === offerId
  );
  return matchingOffer?.qualifyingOptions || [];
}

/**
 * Groups product options with their corresponding prices, taking into account menu type and special offers
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.product - The product object containing options and pricing information
 * @param {string} params.menuType - The type of menu (e.g., 'MEDICAL', 'RECREATIONAL')
 * @param {boolean} params.isKiosk - Whether the menu is being displayed in kiosk mode
 * @param {Object} [params.saleSpecial] - Optional sale special to apply to pricing
 * @param {Object} [params.offerSpecial] - Optional offer special to apply to pricing
 * @param {string | undefined} [params.specialId] - Optional ID of the special to apply
 * @returns {Array<{ value: string, label: string, price: string }>} Array of objects containing all relevant info for each option: : [{ value: '1g', label: '1g', price: '10.00' }, { value: '1/8oz', label: '1/8oz', price: '40.00' }]
 */
export function groupOptionsWithPrices({ product, menuType, isKiosk, saleSpecial, offerSpecial, specialId }) {
  const {
    limitsPerCustomer,
    manualInventory,
    Options: options,
    optionsBelowKioskThreshold,
    optionsBelowThreshold,
    POSMetaData,
  } = product;
  const returnSaleSpecialPrices = _.isUndefined(saleSpecial)
    ? hasSaleSpecialForMenuType({ product, menuType })
    : saleSpecial;

  const prices = getPricesForMenuType({
    product,
    menuType,
    returnSaleSpecialPrices,
  });

  const qualifyingOptions = getOptionsQualifyingForOffer({ product, offerId: specialId });

  return formatWeightOptions({
    isKiosk,
    limitsPerCustomer,
    manualInventory,
    options,
    optionsBelowKioskThreshold,
    optionsBelowThreshold,
    prices,
    weightOnlyLabel: true,
    POSMetaData,
    showQualifyingOptions: !!offerSpecial,
    qualifyingOptions,
  });
}

const parsedProductsWeakMap = new WeakMap();
// this method is evil and should be 100% server side instead
export function parseProduct(product) {
  if (!product) {
    return product;
  }

  const cachedResult = parsedProductsWeakMap.get(product);
  if (cachedResult) {
    return cachedResult;
  }

  // These conversions are needed to support existing shape of product data.
  // Eventually these should be moved to the graphQl layer
  const { effects } = product;
  const parsedProduct = {
    ...product,
    // if object, filter out any key with a falsy value
    effects: _isArray(effects) ? effects : _keys(_pickBy(effects, (effect) => !!effect)),
    description: product.description || product.Description,
    name: product.name || product.Name,
    brand: product.brand || {},
  };

  parsedProductsWeakMap.set(product, parsedProduct);

  return parsedProduct;
}

export function potencyIsDefault(potencyRange = [], category) {
  const defaultRange = _.get(CategoryPotencyRanges, category?.key, []);
  return potencyRange[0] === defaultRange[0] && potencyRange[1] === defaultRange[1];
}

export const potencyWithinRange = (potencyContent, targetRange = []) => {
  if (!Array.isArray(targetRange) || targetRange.length !== 2) {
    return false;
  }

  const [min, max] = targetRange;

  if (!potencyContent) {
    if (min === 0) {
      return true;
    }
    return false;
  }

  // potencyContent is an array of a single value, or a two value range: verify:
  const overMin = potencyContent[0];
  const underMax = potencyContent[1] ?? potencyContent[0];

  return overMin >= min && underMax <= max;
};

export function cartItemId(item) {
  const additionalOption = item.additionalOption ? item.additionalOption : _.head(item.product?.AdditionalOptions);
  return `${item.product.id}-${item.option}${additionalOption ? `-${additionalOption}` : ''}`;
}
